import { Route, Routes } from "react-router-dom";
import { PageLayout } from "~/routes";
import IntegrationsEntryPage from "~/entry-pages/space/integrations/IntegrationsEntryPage";
import IntegrationDetailMrnEntryPage from "~/entry-pages/space/integrations/mrn/[integrationMrn]/IntegrationDetailMrnEntryPage";
import HostedIntegrationListEntryPage from "~/entry-pages/space/integrations/[integrationTypeId]/HostedIntegrationListEntryPage";
import HostedIntegrationEntryPage from "~/entry-pages/space/integrations/[integrationTypeId]/[integrationId]/HostedIntegrationEntryPage";
import HostedIntegrationEditEntryPage from "~/entry-pages/space/integrations/[integrationTypeId]/[integrationId]/edit/HostedIntegrationEditEntryPage";
import IntegrationManagedAgentsEntryPage from "~/entry-pages/space/integrations/managed/IntegrationManagedAgentsEntryPage/IntegrationManagedAgentsEntryPage";
import IntegrationManagedAgentEntryPage from "~/entry-pages/space/integrations/managed/IntegrationManagedAgentsEntryPage/[integrationId]/IntegrationManagedAgentEntryPage";
import AwsIntegrationEntryPage from "~/entry-pages/space/integrations/aws/[integrationId]/AwsIntegrationEntryPage";
import K8IntegrationEntryPage from "~/entry-pages/space/integrations/kubernetes/[integrationId]/K8IntegrationEntryPage";
import K8UpdateConfigurationEntryPage from "~/entry-pages/space/integrations/kubernetes/[integrationId]/edit/K8UpdateConfigurationEntryPage";
import AddIntegrationsEntryPage from "~/entry-pages/space/integrations/add/AddIntegrationEntryPage";
import IntegrationSetupEntryPage from "~/entry-pages/space/integrations/add/[appName]/[integrationTypeId]/IntegrationSetupEntryPage";
import K8AddPoliciesEntryPage from "~/entry-pages/space/integrations/add/mondoo/kubernetes/[integrationId]/K8AddPoliciesEntryPage";
import { OrganizationScope, ScopeType, SpaceScope } from "~/hooks/useScope";
import SecurityPipelineGithubOrgEntryPage from "~/entry-pages/space/integrations/security-pipeline-github/SecurityPipelineGithubOrgEntryPage";
import SecurityPipelineGithubSpaceEntryPage from "~/entry-pages/space/integrations/security-pipeline-github/SecurityPipelineGithubSpaceEntryPage";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import WorkflowEntryPage from "~/entry-pages/space/integrations/workflow/[workflowId]/WorkflowEntryPage";

interface IntegrationsRoutesProps {
  scope: SpaceScope | OrganizationScope;
}

export function IntegrationsRoutes({ scope }: IntegrationsRoutesProps) {
  const { isFeatureEnabled } = useFeatureFlags();
  return (
    <Routes>
      <Route element={<PageLayout />}>
        <Route index element={<IntegrationsEntryPage {...{ scope }} />} />

        {/* Provides a way to link to `HostedIntegrationPage` if you only have an `integrationMrn` string */}
        {/* `/integrations/mrn?:integrationMrn` redirects to `/integrations/:integrationTypeId/:integrationId` */}
        <Route
          path="mrn"
          element={<IntegrationDetailMrnEntryPage {...{ scope }} />}
        />

        <Route
          path=":integrationTypeId"
          element={<HostedIntegrationListEntryPage {...{ scope }} />}
        />

        <Route
          path=":integrationTypeId/:integrationId"
          element={<HostedIntegrationEntryPage {...{ scope }} />}
        />

        <Route
          path=":integrationTypeId/:integrationId/edit"
          element={<HostedIntegrationEditEntryPage {...{ scope }} />}
        />

        {/* not in hosted list yet */}
        <Route
          path="managed"
          element={<IntegrationManagedAgentsEntryPage scope={scope} />}
        />

        <Route
          path="managed/:integrationId/*"
          element={<IntegrationManagedAgentEntryPage {...{ scope }} />}
        />

        <Route
          path="aws/:integrationId/*"
          element={<AwsIntegrationEntryPage {...{ scope }} />}
        />

        <Route
          path="kubernetes/:integrationId/*"
          element={<K8IntegrationEntryPage {...{ scope }} />}
        />

        <Route
          path="kubernetes/:integrationId/edit"
          element={<K8UpdateConfigurationEntryPage {...{ scope }} />}
        />

        {scope.type === ScopeType.Space && (
          <Route
            path="security_pipeline_github/:integrationId/*"
            element={<SecurityPipelineGithubSpaceEntryPage {...{ scope }} />}
          />
        )}
        {scope.type === ScopeType.Organization && (
          <Route
            path="security_pipeline_github/*"
            element={<SecurityPipelineGithubOrgEntryPage {...{ scope }} />}
          />
        )}

        {isFeatureEnabled("Security Pipeline") && (
          <Route path="workflows/:workflowId" element={<WorkflowEntryPage />} />
        )}

        <Route path="add">
          <Route index element={<AddIntegrationsEntryPage {...{ scope }} />} />
          <Route
            path=":appName/:integrationTypeId/*"
            element={<IntegrationSetupEntryPage {...{ scope }} />}
          />

          <Route
            path={`mondoo/kubernetes/:integrationId`}
            element={<K8AddPoliciesEntryPage {...{ scope }} />}
          />
        </Route>
      </Route>
    </Routes>
  );
}
